import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deleteAnnouncement,
  getAnnouncements,
} from '../../../api/announcements.api';
import { queryKeys } from '../../../api/queryKeys';
import {
  ErrorToast,
  SuccessToast,
} from '../../../adminComponents/Toasts/Toasts';
import { handleError } from '../../../helpers/api';
import { useAuth } from '../../../store/auth';
import type {
  Announcement,
  AnnouncementApp,
  AnnouncementPlatform,
} from '../../../types/api/announcements.types';

/** Matches `PAGINATION_LIMIT`, kept local so the hook stays a leaf module. */
const PAGE_SIZE = 20;
const FIRST_PAGE = 1;

export type AnnouncementFilters = {
  query: string;
  app: AnnouncementApp | '';
  platform: AnnouncementPlatform | '';
  /**
   * Empty is "any status". It cannot be a boolean: the param has to be left
   * out entirely, since `active=false` means inactive rather than unfiltered.
   */
  active: '' | 'true' | 'false';
};

const NO_FILTERS: AnnouncementFilters = {
  query: '',
  app: '',
  platform: '',
  active: '',
};

const fetchPage = async (filters: AnnouncementFilters, page: number) => {
  const { data } = await getAnnouncements({
    page,
    limit: PAGE_SIZE,
    query: filters.query || undefined,
    app: filters.app || undefined,
    platform: filters.platform || undefined,
    active: filters.active === '' ? undefined : filters.active === 'true',
  });

  const announcements = data?.data ?? [];

  // A short page is the last one; the endpoint returns no total.
  return {
    announcements,
    nextPage: announcements.length === PAGE_SIZE ? page + 1 : undefined,
  };
};

/**
 * The admin list: filters, paging and the two writes the page can make.
 *
 * One query for the list, keyed by its filters, so the delete and the wizard
 * both refresh it by invalidating a key rather than by calling back into the
 * page to refetch.
 */
export const useAnnouncementsList = () => {
  const user = useAuth((state) => state.user);
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<AnnouncementFilters>(NO_FILTERS);
  const [deleting, setDeleting] = useState(false);

  const list = useInfiniteQuery({
    queryKey: [
      ...queryKeys.admin.announcements.all,
      filters.query,
      filters.app,
      filters.platform,
      filters.active,
    ],
    queryFn: ({ pageParam }) => fetchPage(filters, pageParam),
    getNextPageParam: (last) => last.nextPage,
    initialPageParam: FIRST_PAGE,
    // The list is only reachable once signed in, and the request 401s without
    // a token while the session is still being restored.
    enabled: !!user,
  });

  const announcements = useMemo<Announcement[]>(
    () => list.data?.pages.flatMap((page) => page.announcements) ?? [],
    [list.data]
  );

  // A query keeps its failure in state rather than throwing, and a list that
  // stays empty after a 500 looks exactly like a list with nothing in it. The
  // toast is the only thing that tells those two apart.
  useEffect(() => {
    if (list.error) ErrorToast(handleError(list.error));
  }, [list.error]);

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.announcements.all,
      }),
    [queryClient]
  );

  const updateFilter = useCallback(
    <Key extends keyof AnnouncementFilters>(
      key: Key,
      value: AnnouncementFilters[Key]
    ) => setFilters((prev) => ({ ...prev, [key]: value })),
    []
  );

  const remove = useCallback(
    async (id: number) => {
      try {
        setDeleting(true);
        await deleteAnnouncement(id);
        SuccessToast('Announcement deleted successfully');
        await refresh();
      } catch (error) {
        ErrorToast(handleError(error));
      } finally {
        setDeleting(false);
      }
    },
    [refresh]
  );

  return {
    announcements,
    filters,
    updateFilter,
    remove,
    refresh,
    loading: list.isFetching || deleting,
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: list.fetchNextPage,
  };
};
